import { useEffect, useRef } from "react";
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { LandscapeBackground } from "./components/LandscapeBackground";
import { makeLetterTexture } from "./lib/letterTexture";
import { getTitleTileColor } from "./lib/tileStyle";
import "./index.css";

type Tile = {
  group: THREE.Group;
  block: THREE.Mesh;
  basePosition: THREE.Vector3;
  restingQuaternion: THREE.Quaternion;
  dragQuaternion: THREE.Quaternion;
  dragging: boolean;
  phase: number;
};

type BorderMaterial = THREE.ShaderMaterial & {
  uniforms: {
    viewportSize: { value: THREE.Vector2 };
    thickness: { value: number };
  };
};

const TILE_SIZE = 1.72;

function makeBorderMaterial(): BorderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      viewportSize: { value: new THREE.Vector2(1, 1) },
      thickness: { value: 6 },
    },
    vertexShader: /* glsl */ `
      uniform vec2 viewportSize;
      uniform float thickness;

      void main() {
        vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
        vec4 clipPosition = projectionMatrix * viewPosition;
        vec3 viewNormal = normalize(normalMatrix * normal);
        vec4 normalPosition = projectionMatrix * vec4(viewPosition.xyz + viewNormal, 1.0);
        vec2 projectedNormal =
          normalPosition.xy / normalPosition.w - clipPosition.xy / clipPosition.w;
        vec2 pixelDirection = normalize(projectedNormal * viewportSize);
        vec2 pixelOffset = pixelDirection * thickness * 2.0 / viewportSize;

        clipPosition.xy += pixelOffset * clipPosition.w;
        gl_Position = clipPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      void main() {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
      }
    `,
    side: THREE.BackSide,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  }) as BorderMaterial;
}
function addTile(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  borderMaterial: BorderMaterial,
  letter: string,
  x: number,
  baseRotation: [number, number, number],
  phase: number,
) {
  const group = new THREE.Group();
  group.position.x = x;

  const geometry = new RoundedBoxGeometry(TILE_SIZE, TILE_SIZE, 0.46, 6, 0.13);
  const tileMaterial = new THREE.MeshPhysicalMaterial({
    color: getTitleTileColor(letter),
    roughness: 0.58,
    metalness: 0,
    clearcoat: 0.08,
    clearcoatRoughness: 0.8,
  });
  const block = new THREE.Mesh(geometry, tileMaterial);
  block.renderOrder = 1;
  group.add(block);

  // Draw a screen-space-expanded back-face shell before either tile. Keeping
  // the shell in the tile group makes it follow every tilt and drag, while the
  // shader keeps its visible thickness constant on foreshortened axes.
  const border = new THREE.Mesh(
    geometry,
    borderMaterial,
  );
  border.renderOrder = -1;
  group.add(border);

  const texture = makeLetterTexture(letter, renderer);
  if (texture) {
    const letterMaterial = new THREE.MeshStandardMaterial({
      map: texture,
      transparent: true,
      roughness: 0.72,
      polygonOffset: true,
      polygonOffsetFactor: -2,
    });
    const face = new THREE.Mesh(new THREE.PlaneGeometry(1.48, 1.48), letterMaterial);
    face.position.z = 0.234;
    face.renderOrder = 2;
    group.add(face);
  }

  group.rotation.set(...baseRotation);
  scene.add(group);

  return {
    group,
    block,
    basePosition: group.position.clone(),
    restingQuaternion: group.quaternion.clone(),
    dragQuaternion: group.quaternion.clone(),
    dragging: false,
    phase,
  } satisfies Tile;
}

const ROTATION_MAGNITUDE = 25;

function LetterTiles() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-3, 3, 1.45, -1.45, 0.1, 20);
    camera.position.set(0, 0, 6);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    renderer.setPixelRatio(pixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.setAttribute("aria-hidden", "true");
    host.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xc6bfae, 2.25));

    const keyLight = new THREE.DirectionalLight(0xfffdf6, 4.6);
    keyLight.position.set(-3.5, 5, 7);
    scene.add(keyLight);

    const rimLight = new THREE.DirectionalLight(0xdbe6ff, 1.1);
    rimLight.position.set(5, -2, 4);
    scene.add(rimLight);

    const borderMaterial = makeBorderMaterial();
    const tiles = [
      addTile(
        scene,
        renderer,
        borderMaterial,
        "A",
        -0.94,
        [-0.04, -0.1, -0.055],
        0,
      ),
      addTile(
        scene,
        renderer,
        borderMaterial,
        "Z",
        0.94,
        [0.045, 0.12, 0.045],
        1.7,
      ),
    ];

    const pointer = new THREE.Vector2();
    const rayPointer = new THREE.Vector2();
    const smoothedPointer = new THREE.Vector2();
    const tileScreenPosition = new THREE.Vector3();
    const cursorDirection = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();
    const dragTileCenter = new THREE.Vector2();
    const dragPreviousPosition = new THREE.Vector2();
    const dragCurrentPosition = new THREE.Vector2();
    const dragOffset = new THREE.Vector2();
    const dragAxis = new THREE.Vector3();
    const dragDeltaQuaternion = new THREE.Quaternion();
    const displayQuaternion = new THREE.Quaternion();
    const interactionQuaternion = new THREE.Quaternion();
    const interactionEuler = new THREE.Euler();
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(pointer: fine)");
    const tileByBlock = new Map<THREE.Object3D, Tile>(
      tiles.map((tile) => [tile.block, tile]),
    );
    let activeTile: Tile | null = null;
    let activePointerId: number | null = null;
    let pointerIsActive = false;
    let pointerInfluence = 0;
    let dragTileRadius = 1;

    const setPointerPositionRelativeToTile = (
      clientX: number,
      clientY: number,
      target: THREE.Vector2,
    ) => {
      target.set(
        (clientX - dragTileCenter.x) / dragTileRadius,
        (clientY - dragTileCenter.y) / dragTileRadius,
      );
      return target;
    };

    const updatePointer = (event: PointerEvent) => {
      pointer.set(
        (event.clientX / window.innerWidth - 0.5) * 2,
        (event.clientY / window.innerHeight - 0.5) * 2,
      );
      pointerIsActive =
        !reducedMotion.matches && finePointer.matches && event.pointerType !== "touch";

      const rect = renderer.domElement.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      const normalizedX = (event.clientX - rect.left) / rect.width;
      const normalizedY = (event.clientY - rect.top) / rect.height;
      rayPointer.set(normalizedX * 2 - 1, -(normalizedY * 2 - 1));
      return true;
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || !updatePointer(event)) return;

      raycaster.setFromCamera(rayPointer, camera);
      const hit = raycaster.intersectObjects(tiles.map((tile) => tile.block), false)[0];
      const tile = hit ? tileByBlock.get(hit.object) : undefined;
      if (!tile) return;

      activeTile = tile;
      activePointerId = event.pointerId;
      activeTile.dragging = true;
      activeTile.dragQuaternion.copy(activeTile.group.quaternion);

      const canvasBounds = renderer.domElement.getBoundingClientRect();
      tileScreenPosition.copy(activeTile.group.position).project(camera);
      dragTileCenter.set(
        canvasBounds.left + (tileScreenPosition.x + 1) * 0.5 * canvasBounds.width,
        canvasBounds.top + (1 - tileScreenPosition.y) * 0.5 * canvasBounds.height,
      );
      dragTileRadius = Math.max(
        (TILE_SIZE * activeTile.group.scale.x * canvasBounds.width) /
          (camera.right - camera.left) /
          2,
        36,
      );
      setPointerPositionRelativeToTile(
        event.clientX,
        event.clientY,
        dragPreviousPosition,
      );

      renderer.domElement.setPointerCapture(event.pointerId);
      renderer.domElement.classList.add("is-dragging");
      event.preventDefault();
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!updatePointer(event)) return;
      if (!activeTile || event.pointerId !== activePointerId) return;

      // Accumulate each part of the gesture instead of deriving one fixed
      // orientation from the pointer-down position. This makes curved paths,
      // reversals, and repeated drags leave the tile at more organic angles.
      setPointerPositionRelativeToTile(
        event.clientX,
        event.clientY,
        dragCurrentPosition,
      );
      dragOffset.subVectors(dragCurrentPosition, dragPreviousPosition);
      const dragDistance = dragOffset.length();

      if (dragDistance > 0) {
        dragAxis
          .set(dragOffset.y, dragOffset.x, 0)
          .multiplyScalar(1 / dragDistance);
        dragDeltaQuaternion.setFromAxisAngle(
          dragAxis,
          dragDistance * (Math.PI / 2),
        );
        activeTile.dragQuaternion.premultiply(dragDeltaQuaternion).normalize();
      }
      dragPreviousPosition.copy(dragCurrentPosition);

      event.preventDefault();
    };

    const finishDrag = (pointerId?: number) => {
      if (!activeTile || (pointerId !== undefined && pointerId !== activePointerId)) return;

      activeTile.dragging = false;
      pointerIsActive = false;
      if (
        activePointerId !== null &&
        renderer.domElement.hasPointerCapture(activePointerId)
      ) {
        renderer.domElement.releasePointerCapture(activePointerId);
      }
      activeTile = null;
      activePointerId = null;
      renderer.domElement.classList.remove("is-dragging");
    };

    const handlePointerUp = (event: PointerEvent) => finishDrag(event.pointerId);

    const resetPointer = () => {
      pointerIsActive = false;
      if (activeTile) finishDrag();
    };

    let canvasPixelWidth = 0;
    let canvasPixelHeight = 0;

    const resize = () => {
      const { width, height } = host.getBoundingClientRect();
      if (!width || !height) return;

      const nextPixelWidth = Math.floor(width * pixelRatio);
      const nextPixelHeight = Math.floor(height * pixelRatio);

      // Setting a canvas's backing-store size clears it, even when the value
      // has not changed. Avoid duplicate clears from ResizeObserver and paint
      // synchronously after a real resize so a blank frame cannot slip in
      // between the observer callback and the next animation frame.
      if (
        nextPixelWidth !== canvasPixelWidth ||
        nextPixelHeight !== canvasPixelHeight
      ) {
        renderer.setSize(width, height, false);
        canvasPixelWidth = nextPixelWidth;
        canvasPixelHeight = nextPixelHeight;
      }

      borderMaterial.uniforms.viewportSize.value.set(width, height);
      borderMaterial.uniforms.thickness.value = THREE.MathUtils.clamp(
        height * 0.042,
        4,
        10,
      );
      const halfHeight = 1.45;
      const halfWidth = halfHeight * (width / height);
      camera.left = -halfWidth;
      camera.right = halfWidth;
      camera.top = halfHeight;
      camera.bottom = -halfHeight;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    renderer.domElement.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    document.documentElement.addEventListener("pointerleave", resetPointer);
    window.addEventListener("blur", resetPointer);
    reducedMotion.addEventListener("change", resetPointer);
    finePointer.addEventListener("change", resetPointer);

    let frame = 0;
    const start = performance.now();
    let previousFrame = start;

    const animate = (now: number) => {
      frame = window.requestAnimationFrame(animate);
      const time = (now - start) / 1000;
      const delta = Math.min((now - previousFrame) / 1000, 0.05);
      previousFrame = now;
      const pointerEase = 1 - Math.exp(-18 * delta);
      smoothedPointer.lerp(pointer, pointerEase);
      pointerInfluence = THREE.MathUtils.damp(
        pointerInfluence,
        pointerIsActive ? 1 : 0,
        18,
        delta,
      );
      const canvasBounds = renderer.domElement.getBoundingClientRect();

      tiles.forEach((tile) => {
        const { group, basePosition, phase } = tile;

        if (tile.dragging) {
          // Apply the path-dependent gesture state directly so quick turns and
          // small changes in direction remain visible while dragging.
          group.quaternion.copy(tile.dragQuaternion);
          return;
        }

        const idleX = reducedMotion.matches ? 0 : Math.sin(time * 0.62 + phase) * 0.018;
        const idleY = reducedMotion.matches ? 0 : Math.cos(time * 0.5 + phase) * 0.025;
        const tilt = THREE.MathUtils.degToRad(ROTATION_MAGNITUDE);
        const returnEase = 1 - Math.exp(-12 * delta);

        tileScreenPosition.set(group.position.x, group.position.y, group.position.z).project(camera);
        const tileViewportX =
          ((canvasBounds.left + (tileScreenPosition.x + 1) * 0.5 * canvasBounds.width) /
            window.innerWidth -
            0.5) *
          2;
        const tileViewportY =
          ((canvasBounds.top + (1 - tileScreenPosition.y) * 0.5 * canvasBounds.height) /
            window.innerHeight -
            0.5) *
          2;

        cursorDirection.set(
          smoothedPointer.x - tileViewportX,
          (smoothedPointer.y - tileViewportY) * 0.8,
        );
        if (cursorDirection.lengthSq() > 1) cursorDirection.normalize();
        cursorDirection.multiplyScalar(pointerInfluence);

        interactionEuler.set(
          cursorDirection.y * tilt + idleX,
          cursorDirection.x * tilt + idleY,
          0,
        );
        interactionQuaternion.setFromEuler(interactionEuler);
        displayQuaternion
          .copy(interactionQuaternion)
          .multiply(tile.restingQuaternion);
        group.quaternion.slerp(displayQuaternion, 1 - Math.exp(-18 * delta));
        group.position.x = THREE.MathUtils.lerp(group.position.x, basePosition.x, returnEase);
        group.position.y = THREE.MathUtils.lerp(
          group.position.y,
          basePosition.y + (reducedMotion.matches ? 0 : Math.sin(time * 0.72 + phase) * 0.018),
          returnEase,
        );
        group.position.z = THREE.MathUtils.lerp(group.position.z, basePosition.z, returnEase);
        const restingScale = THREE.MathUtils.damp(group.scale.x, 1, 14, delta);
        group.scale.setScalar(restingScale);
      });

      renderer.render(scene, camera);
    };

    frame = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      document.documentElement.removeEventListener("pointerleave", resetPointer);
      window.removeEventListener("blur", resetPointer);
      reducedMotion.removeEventListener("change", resetPointer);
      finePointer.removeEventListener("change", resetPointer);

      scene.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => {
          if (material instanceof THREE.MeshStandardMaterial && material.map) {
            material.map.dispose();
          }
          material.dispose();
        });
      });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return <div className="tile-stage" ref={hostRef} aria-hidden="true" />;
}

export function App() {
  return (
    <main className="wordmark-page">
      <LandscapeBackground />
      <h1 className="wordmark" aria-label="AZ LAND">
        <LetterTiles />
        <span className="wordmark-text" aria-hidden="true">
          LAND
        </span>
      </h1>
    </main>
  );
}

export default App;
